import React, { useState, useRef } from 'react';
import { IconButton, Tooltip, View, Text, Flex, Button } from '@smarterservices/smarter-ui';
import { useFullScreenMountNode } from '../../proctoring/SessionViewer/contexts/FullScreenContext';

interface ButtonWithConfirmationProps {
  // Main button props
  onConfirm: () => void;
  onCancel?: () => void;
  children: React.ReactNode;
  size?: 'small' | 'medium' | 'large';
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger';
  margin?: string;
  screenReaderLabel?: string;
  disabled?: boolean;
  
  // Confirmation dialog props
  confirmationText?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  confirmButtonColor?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger';
  
  // Tooltip props (allow overrides)
  tooltipProps?: {
    placement?: 'top' | 'bottom' | 'start' | 'end';
    color?: 'primary' | 'primary-inverse';
    offsetX?: number;
    offsetY?: number;
    on?: ('click' | 'hover' | 'focus')[];
  };
  
  // View props for confirmation dialog
  viewProps?: {
    padding?: string;
    maxWidth?: string;
    textAlign?: 'start' | 'center' | 'end';
  };
  
  // Text props for confirmation message
  textProps?: {
    size?: 'x-small' | 'small' | 'medium' | 'large' | 'x-large';
    as?: 'div' | 'span' | 'p';
  };
  
  // Flex props for button container
  flexProps?: {
    direction?: 'row' | 'column';
    justifyItems?: 'start' | 'center' | 'end' | 'space-around' | 'space-between';
  };
  
  // Individual button styling
  confirmButtonProps?: {
    size?: 'small' | 'medium' | 'large';
    margin?: string;
    padding?: string;
  };
  
  cancelButtonProps?: {
    size?: 'small' | 'medium' | 'large';
    margin?: string;
    padding?: string;
  };
}

export function ButtonWithConfirmation({
  onConfirm,
  onCancel,
  children,
  size = 'small',
  color = 'primary',
  margin = 'none x-small none none',
  screenReaderLabel = 'Action Button',
  disabled = false,
  confirmationText = 'Are you sure?',
  confirmButtonText = 'Yes',
  cancelButtonText = 'No',
  confirmButtonColor = 'primary',
  tooltipProps = {},
  viewProps = {},
  textProps = {},
  flexProps = {},
  confirmButtonProps = {},
  cancelButtonProps = {},
}: ButtonWithConfirmationProps) {
  const [showConfirmationTooltip, setShowConfirmationTooltip] = useState(false);
  const tooltipTriggerRef = useRef<HTMLElement>(null);
  
  // Get the appropriate mount node for full screen support
  const mountNode = useFullScreenMountNode();

  const handleConfirm = () => {
    setShowConfirmationTooltip(false);
    onConfirm();
  };

  const handleCancel = () => {
    setShowConfirmationTooltip(false);
    onCancel?.();
  };

  const handleToggleTooltip = () => {
    if (!disabled) {
      setShowConfirmationTooltip(!showConfirmationTooltip);
    }
  };

  // Merge default tooltip props with overrides
  const mergedTooltipProps = {
    placement: 'bottom' as const,
    color: 'primary' as const,
    offsetY: 5,
    on: ['click'] as const,
    ...tooltipProps,
  };

  // Merge default view props with overrides
  const mergedViewProps = {
    padding: 'xx-small',
    maxWidth: '200px',
    textAlign: 'center' as const,
    ...viewProps,
  };

  // Merge default text props with overrides
  const mergedTextProps = {
    size: 'small' as const,
    as: 'div' as const,
    ...textProps,
  };

  // Merge default flex props with overrides
  const mergedFlexProps = {
    direction: 'row' as const,
    justifyItems: 'center' as const,
    ...flexProps,
  };

  // Merge default button props with overrides
  const mergedConfirmButtonProps = {
    size: 'small' as const,
    padding: 'xx-small',
    ...confirmButtonProps,
  };

  const mergedCancelButtonProps = {
    size: 'small' as const,
    padding: 'xx-small',
    ...cancelButtonProps,
  };

  return (
    <Tooltip
      renderTip={
        <View
          as="div"
          padding={mergedViewProps.padding}
          maxWidth={mergedViewProps.maxWidth}
          textAlign={mergedViewProps.textAlign}
        >
          <Text 
            as={mergedTextProps.as} 
            size={mergedTextProps.size}
          >
            {confirmationText}
          </Text>
          <Flex 
            direction={mergedFlexProps.direction}
            justifyItems={mergedFlexProps.justifyItems}
          >
            <Flex.Item padding={mergedConfirmButtonProps.padding}>
              <Button
                size={mergedConfirmButtonProps.size}
                color={confirmButtonColor}
                onClick={handleConfirm}
                margin={mergedConfirmButtonProps.margin}
              >
                {confirmButtonText}
              </Button>
            </Flex.Item>
            <Flex.Item padding={mergedCancelButtonProps.padding}>
              <Button
                size={mergedCancelButtonProps.size}
                onClick={handleCancel}
                margin={mergedCancelButtonProps.margin}
              >
                {cancelButtonText}
              </Button>
            </Flex.Item>
          </Flex>
        </View>
      }
      on={mergedTooltipProps.on}
      placement={mergedTooltipProps.placement}
      color={mergedTooltipProps.color}
      offsetX={mergedTooltipProps.offsetX}
      offsetY={mergedTooltipProps.offsetY}
      mountNode={mountNode}
      isShowingContent={showConfirmationTooltip}
      onShowContent={() => setShowConfirmationTooltip(true)}
      onHideContent={() => setShowConfirmationTooltip(false)}
    >
      <IconButton
        size={size}
        color={color}
        margin={margin}
        screenReaderLabel={screenReaderLabel}
        onClick={handleToggleTooltip}
        disabled={disabled}
        elementRef={(el) => {
          tooltipTriggerRef.current = el;
        }}
      >
        {children}
      </IconButton>
    </Tooltip>
  );
}

export default ButtonWithConfirmation;